import { useState } from "react";
import { useUser } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { showToast } from "@/lib/toast";
import { Upload } from "lucide-react";

interface UploadInvoiceFormProps {
  onSuccess?: () => void;
}

export const UploadInvoiceForm = ({ onSuccess }: UploadInvoiceFormProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const user = useUser();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      showToast.error("Please upload a PDF file");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setIsUploading(true);
    try {
      const fileExt = selectedFile.name.split(".").pop();
      const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("invoices")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from("invoices").insert({
        user_id: user.id,
        file_name: selectedFile.name,
        file_path: filePath,
        invoice_number: `INV-${Date.now()}`,
        amount: 0,
        description: "Uploaded invoice - details pending",
        due_date: new Date().toISOString(),
        recipient_name: "Pending",
        recipient_email: "pending@example.com",
        recipient_address: "Pending",
      });

      if (dbError) throw dbError;

      showToast.success("Invoice uploaded successfully");

      // Reset the form
      setSelectedFile(null);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (input) input.value = "";
      
      onSuccess?.(); // Call the onSuccess callback if provided
    } catch (error: any) {
      console.error("Error:", error);
      showToast.error(error.message || "Failed to upload invoice");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Upload Invoice</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="w-[calc(100%-120px)] h-14">
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="h-14"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Upload a PDF file of your invoice. Maximum file size: 5MB
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          size="lg"
        >
          {isUploading ? (
            <>
              <Upload className="animate-bounce" />
              Uploading...
            </>
          ) : (
            <>
              <Upload />
              Upload Invoice
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
